import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  loginUser,
  sendLoginOtp,
  verifyLoginOtp,
} from "../context/actions";
import { AuthContext } from "../context/AuthContext";
import { SESSION_EXPIRED_PARAM } from "../utils/tokenExpiry";
import "./LoginPage.css";

const LOGO_SRC = "/assets/brand/jaipuria-it360-logo-trimmed.jpg";

const IconUser = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconMail = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconLock = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const IconEyeOff = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const IconEyeOn = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconArrow = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const IconAlert = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const IconHeadset = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
    <path d="M21 15a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-1a2 2 0 0 1 2-2h3v3z" />
    <path d="M3 15a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-1a2 2 0 0 0-2-2H3v3z" />
    <path d="M15 20a2 2 0 0 1-2 2h-2" />
  </svg>
);

const IconDatabase = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
    <path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
  </svg>
);

const IconChecklist = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M8 12l2.5 2.5L16 9" />
  </svg>
);

const IconBarChart = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const IconRefresh = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <polyline points="21 3 21 9 15 9" />
  </svg>
);

const Badge360 = ({ className }) => (
  <div className={className}>
    <div className="lp-badge-dash-ring" />
    <div className="lp-badge-inner">
      <span className="lp-badge-refresh"><IconRefresh /></span>
      <div className="lp-badge-text">360°</div>
      <div className="lp-badge-sub">Verified</div>
    </div>
  </div>
);

const LoginPage = () => {
  const [activeTab, setActiveTab] = useState("password");

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  // Sent here by an expired session (utils/tokenExpiry.js) -- say why.
  const [error, setError] = useState(() =>
    new URLSearchParams(window.location.search).get(SESSION_EXPIRED_PARAM)
      ? "Your session has expired. Please log in again."
      : ""
  );
  const [success, setSuccess] = useState("");

  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  const completeLogin = (result) => {
    console.log("[Login] completeLogin result:", result);
    if (result?.user) {
      localStorage.setItem("user", JSON.stringify(result.user));
      localStorage.setItem("token", result.token);
      dispatch({ type: "LOGIN_SUCCESS", payload: result });
      console.log("[Login] navigating to:", `/${result.user.role}/home`);
      navigate(`/${result.user.role}/home`);
    } else {
      console.log("[Login] no user in result, login failed");
      setError("Login failed. Please try again.");
    }
  };

  const handlePasswordLogin = async () => {
    console.log("[Login] handlePasswordLogin called with:", { identifier, password });
    if (!identifier.trim() || !password.trim()) {
      console.log("[Login] missing identifier or password");
      setError("Please enter username/email and password.");
      return;
    }
    setError(""); setSuccess(""); setLoading(true);
    try {
      const result = await loginUser(dispatch, {
        login: identifier.trim(),
        username: identifier.trim(),
        email: identifier.trim(),
        password,
      });
      console.log("[Login] loginUser response:", result);
      completeLogin(result);
    } catch (err) {
      console.error("[Login] loginUser error:", err, err?.response?.data);
      setError(err?.response?.data?.message || "Invalid username/email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!email.trim()) { setError("Please enter your email."); return; }
    setError(""); setSuccess(""); setOtpLoading(true);
    try {
      const res = await sendLoginOtp(email.trim());
      setOtpSent(true);
      setSuccess(res?.message || "OTP sent successfully on your email.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send OTP. Please check your email.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!email.trim() || !otp.trim()) { setError("Please enter email and OTP."); return; }
    setError(""); setSuccess(""); setLoading(true);
    try {
      const result = await verifyLoginOtp(dispatch, { email: email.trim(), otp: otp.trim() });
      completeLogin(result);
    } catch (err) {
      setError(err?.response?.data?.message || "Invalid or expired OTP.");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key !== "Enter") return;
    if (activeTab === "password") handlePasswordLogin();
    else if (otpSent) handleVerifyOtp();
    else handleSendOtp();
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError("");
    setSuccess("");
  };

  return (
    <div className="lp-root">
      {/* ── TOP BAR ── */}
      <div className="lp-topbar">
        <img className="lp-topbar-logo" src={LOGO_SRC} alt="Jaipuria IT360 Desk" />
        <div className="lp-topbar-links">
          {/* <a className="lp-topbar-link" href="#help">Help Center</a> */}
          <a className="lp-topbar-link lp-topbar-link-strong" href="#contact">Contact Support</a>
        </div>
      </div>

      <div className="lp-split">
        {/* ── LEFT PANEL ── */}
        <div className="lp-left">
        <div className="lp-form-shell">
          <div className="lp-eyebrow-wrap">
            <div className="lp-eyebrow">Account Access</div>
            <div className="lp-eyebrow-rule" />
          </div>

          <h1 className="lp-title">Sign in.</h1>

          <div className="lp-subnav">
            <button
              type="button"
              className={`lp-subnav-item ${activeTab === "password" ? "active" : ""}`}
              onClick={() => switchTab("password")}
            >
              {activeTab === "password" && <span className="lp-subnav-dot" />}
              Login
            </button>
            <span className="lp-subnav-sep">/</span>
            <button
              type="button"
              className={`lp-subnav-item ${activeTab === "otp" ? "active" : ""}`}
              onClick={() => switchTab("otp")}
            >
              {activeTab === "otp" && <span className="lp-subnav-dot" />}
              Login with OTP
            </button>
          </div>

          {error && (
            <div className="lp-error">
              <IconAlert />
              {error}
            </div>
          )}

          {success && (
            <div className="lp-success">{success}</div>
          )}

          {activeTab === "password" && (
            <>
              <div className="lp-field">
                <label className="lp-label" htmlFor="lp-identifier">Username</label>
                <div className="lp-input-wrap">
                  <input
                    id="lp-identifier"
                    className="lp-input"
                    type="text"
                    placeholder="jane.doe"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    onKeyDown={onKey}
                    autoComplete="username"
                    autoFocus
                  />
                  <span className="lp-input-icon-right"><IconUser /></span>
                </div>
              </div>

              <div className="lp-field">
                <label className="lp-label" htmlFor="lp-password">Password</label>
                <div className="lp-input-wrap">
                  <input
                    id="lp-password"
                    className="lp-input"
                    type={showPw ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={onKey}
                    autoComplete="current-password"
                  />
                  <button
                    className="lp-pw-toggle"
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPw((v) => !v);
                    }}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <IconEyeOff /> : <IconEyeOn />}
                  </button>
                </div>
              </div>

              <div className="lp-row">
                <label className="lp-checkbox">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <button type="button" className="lp-forgot-link" onClick={() => switchTab("otp")}>
                  Login with OTP instead
                </button>
              </div>

              <button
                className="lp-btn"
                onClick={handlePasswordLogin}
                disabled={loading}
                type="button"
              >
                {loading ? (
                  <><span className="lp-spinner" /> Signing in…</>
                ) : (
                  <>Sign In <IconArrow /></>
                )}
              </button>
            </>
          )}

          {activeTab === "otp" && (
            <>
              <div className="lp-field">
                <label className="lp-label" htmlFor="lp-email">Registered Email</label>
                <div className="lp-input-wrap">
                  <input
                    id="lp-email"
                    className="lp-input"
                    type="email"
                    placeholder="jane.doe@jaipuria.edu.in"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={onKey}
                    autoComplete="email"
                  />
                  <span className="lp-input-icon-right"><IconMail /></span>
                </div>
              </div>

              {otpSent && (
                <div className="lp-field">
                  <label className="lp-label" htmlFor="lp-otp">One-Time Code</label>
                  <div className="lp-input-wrap">
                    <input
                      id="lp-otp"
                      className="lp-input"
                      type="text"
                      maxLength="6"
                      placeholder="6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      onKeyDown={onKey}
                    />
                    <span className="lp-input-icon-right"><IconLock /></span>
                  </div>
                </div>
              )}

              {!otpSent ? (
                <button className="lp-btn" onClick={handleSendOtp} disabled={otpLoading} type="button">
                  {otpLoading ? (
                    <><span className="lp-spinner" /> Sending OTP…</>
                  ) : (
                    <>Send OTP <IconArrow /></>
                  )}
                </button>
              ) : (
                <>
                  <button className="lp-btn" onClick={handleVerifyOtp} disabled={loading} type="button">
                    {loading ? (
                      <><span className="lp-spinner" /> Verifying…</>
                    ) : (
                      <>Verify & Sign In <IconArrow /></>
                    )}
                  </button>
                  <button className="lp-link-btn" onClick={handleSendOtp} disabled={otpLoading} type="button">
                    Resend OTP
                  </button>
                </>
              )}
            </>
          )}
        </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="lp-right">
          <span className="lp-deco-ring" aria-hidden="true" />
          <span className="lp-deco-ring-dotted" aria-hidden="true" />

          <div className="lp-right-top">
            <div className="lp-status-card">
              <img className="lp-status-logo" src={LOGO_SRC} alt="Jaipuria IT360 Desk" />
              <div className="lp-status-line">
                <span className="lp-status-dot" />
                All systems operational
              </div>
            </div>
            <div className="lp-topbars" aria-hidden="true">
              <span /><span /><span />
            </div>
          </div>

          <div className="lp-feature-row">
            <div className="lp-feature">
              <span className="lp-feature-icon"><IconHeadset /></span>
              <span className="lp-feature-label">Support</span>
            </div>
            <div className="lp-feature">
              <span className="lp-feature-icon"><IconDatabase /></span>
              <span className="lp-feature-label">Assets</span>
            </div>
            <div className="lp-feature">
              <span className="lp-feature-icon"><IconChecklist /></span>
              <span className="lp-feature-label">Projects</span>
            </div>
            <div className="lp-feature">
              <span className="lp-feature-icon"><IconBarChart /></span>
              <span className="lp-feature-label">Reports</span>
            </div>
          </div>

          {/* Mobile-only: rendered inline (in the maroon flow) instead of
              the desktop/tablet overlay below, which is deliberately
              rendered outside .lp-right so its center can straddle the
              cream/maroon diagonal -- .lp-right's clip-path has no
              diagonal on mobile anyway (see the max-width:860px rules),
              so the overlay trick isn't needed there and would only
              disconnect the badge from the maroon panel's visual flow. */}
          <Badge360 className="lp-badge-360 lp-badge-inline" />

          <div className="lp-right-bottom">
            <h2 className="lp-right-title">Jaipuria IT360 Desk</h2>
            <p className="lp-right-desc">
              One platform for IT Support, Assets &amp; Projects Management
            </p>
          </div>

          <span className="lp-dot lp-dot-1" />
          <span className="lp-dot lp-dot-2" />

          <div className="lp-footer">
            © 2026 Jaipuria IT360 Desk
            <span className="lp-footer-fill" />
            Encrypted Session
          </div>
        </div>

        {/* Overlay: sits on .lp-split, straddling the cream/maroon diagonal.
            Rendered outside .lp-right on purpose -- .lp-right has a
            clip-path for the diagonal edge, which would clip off the half
            of this badge that needs to sit on the cream side. Hidden on
            mobile in favor of the inline copy above. */}
        <Badge360 className="lp-badge-360 lp-badge-overlay" />
      </div>
    </div>
  );
};

export default LoginPage;
